import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConversationBubble from './ConversationBubble';
import VoiceConfirmCard, { ConfirmCardData } from './VoiceConfirmCard';
import VoiceQuickPicks, { VoiceQuickPickOption } from './VoiceQuickPicks';
import HistoryModal from './HistoryModal';
import Icon from './ui/Icon';
import { useOverlayFocusTrap } from '../hooks/useOverlayFocusTrap';
import type { AssistantSuggestedQueryPill } from '../constants/assistantSuggestedQueries';
import type { VoiceMessage, VoiceSession } from '../utils/voiceSessionStorage';
import './GlobalVoiceAssistantUI.css';

interface GlobalVoiceAssistantUIProps {
  messages: VoiceMessage[];
  isProcessing: boolean;
  isRecording: boolean;
  activeComponent?: string | null;
  componentData?: any;
  showHistoryModal: boolean;
  pastSessions: VoiceSession[];
  selectedPastSession?: VoiceSession | null;
  isMinimized: boolean;
  currentTranscript?: string;
  confirmCard?: ConfirmCardData | null;
  quickPicks?: VoiceQuickPickOption[];
  dynamicSuggestedQueries?: string[];
  pageSuggestedQueries?: AssistantSuggestedQueryPill[];
  onQuickPickSelect?: (option: VoiceQuickPickOption) => void;

  onClose: () => void;
  onMinimize?: () => void;
  onMaximize?: () => void;
  onStartVoiceInput: () => void;
  onStopVoiceInput?: () => void;
  onTranscription: (text: string) => void;
  onProcessingChange: (isProcessing: boolean) => void;
  onOpenHistory?: (show?: boolean) => void;
  onCloseHistoryModal?: () => void;
  onSelectPastSession?: (session: VoiceSession) => void;
  onLoadSession?: (session: VoiceSession) => void;
  onLoadSelectedSession?: () => void;
  onStartNewChat?: () => void;
  onClearHistory?: () => void;
  onCloseComponent?: () => void;
  onConfirmTransaction?: () => void;
  onConfirmQuotation?: () => void;

  assistantContext?: any;
  company?: any;
  notify?: (message: string, type?: string) => void;
}

const GlobalVoiceAssistantUI: React.FC<GlobalVoiceAssistantUIProps> = ({
  messages,
  isProcessing,
  isRecording,
  showHistoryModal,
  pastSessions,
  selectedPastSession,
  isMinimized,
  currentTranscript,
  confirmCard,
  quickPicks = [],
  dynamicSuggestedQueries = [],
  pageSuggestedQueries = [],
  onQuickPickSelect,
  onClose,
  onMinimize,
  onMaximize,
  onStartVoiceInput,
  onStopVoiceInput,
  onTranscription,
  onProcessingChange,
  onOpenHistory,
  onCloseHistoryModal,
  onSelectPastSession,
  onLoadSession,
  onLoadSelectedSession,
  onStartNewChat,
  onClearHistory,
  notify,
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [textMode, setTextMode] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [waveLevels, setWaveLevels] = useState<number[]>([]);

  useOverlayFocusTrap(overlayRef, !isMinimized);

  useEffect(() => {
    const scrollEl = chatScrollRef.current;
    if (!scrollEl) return;
    scrollEl.scrollTop = scrollEl.scrollHeight;
  }, [messages, currentTranscript, isProcessing]);

  const hasChatContent = messages.length > 0 || Boolean(currentTranscript?.trim()) || isProcessing;

  const suggestionPills = pageSuggestedQueries;

  const handleSuggestedQuery = (query: string) => {
    if (isProcessing || isRecording) return;
    onTranscription(query);
  };

  // Live microphone waveform fed by GlobalVoiceInputHandler.
  useEffect(() => {
    const handleWaveform = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setWaveLevels(Array.isArray(detail?.levels) ? detail.levels : []);
    };
    window.addEventListener('voiceWaveform', handleWaveform as EventListener);
    return () => window.removeEventListener('voiceWaveform', handleWaveform as EventListener);
  }, []);

  const handleHistoryClose = () => {
    onCloseHistoryModal?.() ?? onOpenHistory?.(false);
  };

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = textInput.trim();
    if (!trimmed || isProcessing) return;
    setTextInput('');
    setTextMode(false);
    onTranscription(trimmed);
  };

  // Keep the full thread in the scroll area; newest messages sit at the bottom near the mic.
  const threadMessages = messages;

  if (isMinimized) {
    return (
      <button type="button" className="gva-fab" onClick={onMaximize} title="Open voice assistant">
        AI
      </button>
    );
  }

  return (
    <div ref={overlayRef} className={`gva-overlay${textMode ? ' gva-overlay--text-mode' : ''}`}>
      {/* Header */}
      <header className="gva-header">
        <div className="gva-header-brand">
          <div className="gva-header-logo">
            <Icon name="app-logo" size={24} />
          </div>
          <h1 className="gva-header-title">EZAccounting.ai</h1>
        </div>
        <div className="gva-header-actions">
          <button type="button" className="gva-icon-btn" onClick={() => onOpenHistory?.(true)} title="Chat history" aria-label="Chat history">
            <Icon name="chat" size={20} />
          </button>
          {onMinimize && (
            <button type="button" className="gva-icon-btn" onClick={onMinimize} title="Minimize" aria-label="Minimize">
              <Icon name="minimize" size={20} />
            </button>
          )}
          <button type="button" className="gva-icon-btn" onClick={onClose} title="Close" aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </div>
      </header>

      {/* Main */}
      <main className={`gva-main${hasChatContent ? ' gva-main--active-chat' : ''}`}>
        <div className="gva-main-top">
          <div className={`gva-orb-container${hasChatContent ? ' gva-orb-container--compact' : ''}`}>
            <div className="gva-orb-ring-outer" />
            <div className="gva-orb-ring-inner" />
            <div className={`gva-orb-core ${isRecording ? 'recording' : ''}`}>
              <Icon name={isRecording ? 'mic' : 'app-logo'} size={hasChatContent ? (isRecording ? 32 : 36) : (isRecording ? 48 : 56)} />
            </div>
          </div>

          {isRecording && (
            <div className="gva-waveform" aria-hidden="true">
              {(waveLevels.length ? waveLevels : new Array(28).fill(0)).map((level, i) => (
                <span
                  key={i}
                  className="gva-waveform-bar"
                  style={{ height: `${Math.max(8, Math.min(100, level * 100))}%` }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="gva-chat-scroll" ref={chatScrollRef}>
          {threadMessages.length === 0 && !isRecording && !isProcessing && (
            <p className="gva-chat-empty">Tap the mic and speak, or type a message below.</p>
          )}

          {threadMessages.map((msg) => (
            <ConversationBubble key={msg.id} message={msg} dark />
          ))}

          {isRecording && currentTranscript?.trim() && (
            <div className="gva-bubble-row gva-bubble-row-user gva-live-user-row">
              <div className="gva-bubble-col gva-bubble-col-user">
                <p className="gva-bubble-label">You</p>
                <div className="gva-bubble gva-bubble-user gva-bubble-dark gva-bubble-live">
                  <p>{currentTranscript}</p>
                </div>
                <span className="gva-bubble-time gva-bubble-time-live">Listening…</span>
              </div>
            </div>
          )}

          {isProcessing && (
            <div className="gva-processing">
              <div className="gva-processing-dot" />
              <div className="gva-processing-dot" />
              <div className="gva-processing-dot" />
            </div>
          )}

          {confirmCard && <VoiceConfirmCard data={confirmCard} variant="inline" />}

          <div ref={chatEndRef} />
        </div>
      </main>

      {/* Footer controls */}
      {quickPicks.length > 0 && onQuickPickSelect && (
        <VoiceQuickPicks
          options={quickPicks}
          onSelect={onQuickPickSelect}
          disabled={isProcessing}
        />
      )}

      <footer className={`gva-footer${textMode ? ' gva-footer--text-mode' : ''}`}>
        {suggestionPills.length > 0 && (
          <div className="gva-suggestions">
            <p className="gva-suggestions-label">{t('dashboard.assistant.suggestedQueries')}</p>
            <div className="gva-suggestion-pills">
              {suggestionPills.map(({ label, command, dynamic }) => (
                <button
                  key={command}
                  type="button"
                  className={`gva-suggestion-pill${dynamic ? ' gva-suggestion-pill--dynamic' : ''}`}
                  onClick={() => handleSuggestedQuery(command)}
                  disabled={isProcessing || isRecording}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {textMode && (
          <form className="gva-text-input-bar" onSubmit={handleTextSubmit}>
            <input
              className="gva-text-input"
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Type your message…"
              autoFocus
              disabled={isProcessing}
            />
            <button type="submit" className="gva-text-send" disabled={isProcessing || !textInput.trim()}>
              Send
            </button>
          </form>
        )}

        <div className="gva-controls">
          <button
            type="button"
            className={`gva-control-btn gva-keyboard-btn${textMode ? ' active' : ''}`}
            onClick={() => setTextMode((v) => !v)}
            title="Keyboard input"
            aria-label="Keyboard input"
            aria-pressed={textMode}
          >
            <Icon name="keyboard" size={24} />
            <span className="gva-control-label">Type</span>
          </button>

          <button
            type="button"
            className={`gva-mic-btn ${isRecording ? 'recording' : ''}`}
            onClick={() => {
              if (isRecording) {
                onStopVoiceInput?.();
              } else if (!isProcessing) {
                onStartVoiceInput();
              }
            }}
            disabled={isProcessing && !isRecording}
            title={isRecording ? 'Stop recording' : 'Start recording'}
            aria-label={isRecording ? 'Stop recording' : 'Start recording'}
            aria-pressed={isRecording}
          >
            {isRecording && <span className="gva-mic-ring" />}
            <Icon name={isRecording ? 'mic-stop' : 'mic'} size={24} className="gva-mic-icon" />
          </button>

          <button
            type="button"
            className="gva-control-btn"
            onClick={() => document.getElementById('voice-assistant-file-input')?.click()}
            title="Attach file"
            aria-label="Attach file"
          >
            <Icon name="attach" size={24} />
          </button>
        </div>

        <input
          type="file"
          id="voice-assistant-file-input"
          accept=".csv,.tsv,.txt,.xlsx,.xls"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              window.dispatchEvent(new CustomEvent('voiceAssistantFileUpload', { detail: { file } }));
            }
            e.target.value = '';
          }}
        />

      </footer>

      {/* History panel */}
      {showHistoryModal && (
        <HistoryModal
          isOpen={showHistoryModal}
          onClose={handleHistoryClose}
          pastSessions={pastSessions}
          selectedSession={selectedPastSession}
          onSelectSession={onSelectPastSession || (() => {})}
          onLoadSession={onLoadSession || (() => {})}
          onLoadSelectedSession={onLoadSelectedSession || (() => {})}
          onStartNewChat={onStartNewChat || (() => {})}
          onClearHistory={onClearHistory || (() => {})}
          messages={messages}
          notify={notify}
        />
      )}

    </div>
  );
};

export default GlobalVoiceAssistantUI;
